<script setup>
/**
 * The one Squash Levels line on a player's sheet (PlayerCard) and profile
 * (owner, 3 Nov 2026, SQUASH_LEVELS_CARDS_PLAN.md), drawn only when the API
 * sent `squashLevelsSharing` for that player. The words come from
 * squashLevelsStatusFor.
 *
 *   * Someone else: the line only.
 *   * You, not sharing: Turn on, which opens the question in place
 *     (SquashLevelsTurnOnDialog). Nothing flips silently.
 *   * You, sharing: a plain switch; off is instant (PUT auth/me/externalServices).
 *
 * No link to the profile. Emits `changed(status)` with what the card should
 * show once the API has accepted a change, so the parent can redraw the chip
 * and refetch.
 */
import {computed, ref} from 'vue';
import {endpoints} from '~/network/endpoints.js';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {useAuthService} from '~/composables/useAuthService';
import {
  SQUASH_LEVELS_TURN_ON_LABEL,
  squashLevelsServicesBody,
  squashLevelsSharingAfterSave,
  squashLevelsStatusFor,
} from '~/composables/useSquashLevelsCards';
import SquashLevelsTurnOnDialog from '~/components/squashLevels/SquashLevelsTurnOnDialog.vue';

const props = defineProps({
  person: {type: Object, default: null},
  isMe: {type: Boolean, default: false},
});
const emit = defineEmits(['changed']);

const {httpRequest} = useHttpRequest();
const {updateLocalUser} = useAuthService();

const status = computed(() => squashLevelsStatusFor(props.person, {isMe: props.isMe}));
const turnOnOpen = ref(false);
const switching = ref(false);
const error = ref(null);

const onSaved = (on) => emit('changed', squashLevelsSharingAfterSave(on));

const turnOff = async () => {
  if (switching.value) return;
  switching.value = true;
  error.value = null;
  try {
    await httpRequest(endpoints.auth.meServices, squashLevelsServicesBody(false));
    await updateLocalUser();
    emit('changed', squashLevelsSharingAfterSave(false));
  } catch (e) {
    error.value = e?.global?.[0] || "Couldn't save that. Please try again.";
  } finally {
    switching.value = false;
  }
};
</script>

<template>
  <div v-if="status" class="sls" data-testid="squash-levels-status">
    <div class="sls-row" :class="{'sls-row--off': !status.sharing}">
      <span class="sls-ic" aria-hidden="true"><img src="/images/sl_icon.png" alt="" width="22" height="22" class="sls-img"/></span>
      <span class="sls-body">
        <span class="sls-t">{{ status.title }}</span>
        <span v-if="status.subtitle" class="sls-s">{{ status.subtitle }}</span>
      </span>
      <v-btn v-if="status.action === 'turnOn'" color="primary" variant="flat" size="small" rounded="pill"
             class="sls-turn-on" data-testid="squash-levels-turn-on" @click.stop="turnOnOpen = true">
        {{ SQUASH_LEVELS_TURN_ON_LABEL }}
      </v-btn>
      <v-switch v-else-if="status.action === 'switch'" :model-value="true" :loading="switching" :disabled="switching"
                color="success" density="compact" hide-details inset class="sls-switch"
                :aria-label="status.title" data-testid="squash-levels-switch"
                @update:model-value="(v) => { if (!v) turnOff(); }"/>
    </div>
    <div v-if="error" class="sls-error">{{ error }}</div>
    <squash-levels-turn-on-dialog v-if="status.action === 'turnOn'" v-model="turnOnOpen" @saved="onSaved"/>
  </div>
</template>

<style scoped lang="scss">
/* The match page's Squash Levels row shape: a 42px white tile, a
   bold line and a line under it. */
.sls-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  background: rgb(var(--v-theme-surface));
  text-align: left;
  min-width: 0;
}
.sls-ic {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid rgb(var(--v-theme-borderLight));
}
.sls-img { display: block; width: 22px; height: 22px; }
.sls-row--off .sls-img { filter: grayscale(1); opacity: .5; }
.sls-body { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.sls-t { font-size: 13.5px; font-weight: 600; line-height: 1.35; color: rgb(var(--v-theme-text)); }
.sls-s { font-size: 11.5px; line-height: 1.45; color: rgb(var(--v-theme-textSubtle)); }
.sls-turn-on, .sls-switch { flex: 0 0 auto; }
.sls-error { margin-top: 6px; font-size: 12px; color: rgb(var(--v-theme-error)); }
</style>
